
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import Icon from "@/components/UI/Icon.vue";
import { getBlocksAndPutIntoStore } from "@/lib/global-blocks/global-blocks";
import { configStore, uiStore } from "@/store/store";
import { getDocumentUrlFromId } from "@/lib/wordpress/load";
import { Control } from "@/types/controls";
import { PropertiesData } from "@/types/tree";
import {
  makeControlsFromComponent,
  makeTargetsFromComponent
} from "@/lib/components/controls";
import { Target } from "@/types/components";
import NiceButton from "@/components/UI/NiceButton.vue";
import { isEqual } from "lodash";
import {
  stopInlineEditing,
  startInlineEditing
} from "@/lib/inline-editor/inline-editor";

@Component({
  components: { Icon, NiceButton }
})
export default class ComponentInput extends Vue {
  @Prop()
  value:
    | { componentId: number; properties: PropertiesData; targets?: Target[] }
    | undefined;

  @Prop()
  path!: string;

  loading = false;
  componentsFutureFunctionality = false;

  beforeCreate() {
    // @ts-ignore
    this.$options.components.ControlsTree =
      require("@/components/Reusable/ControlsTree/ControlsTree.vue").default;
  }

  reloadBlocks() {
    this.loading = true;
    this.loadBlocks();
  }

  async loadBlocks() {
    await getBlocksAndPutIntoStore();
    this.loading = false;

    // Always update targets to catch new fields
    this.recalculateTargets();
  }

  recalculateTargets() {
    if (this.value?.componentId) {
      const possiblyNewTargets = this.makeTargets(this.value.componentId);
      const hasTargetsChanged = !isEqual(
        this.value.targets,
        possiblyNewTargets
      );

      if (!hasTargetsChanged) return;

      this.$emit("input", {
        ...this.value,
        targets: possiblyNewTargets
      });
    }
  }

  get allComponents() {
    return configStore.globalBlocks;
  }

  get selectedComponent() {
    return this.allComponents.find(
      component => component.id === this.value?.componentId
    );
  }

  get items() {
    return this.allComponents.map(component => {
      return {
        text: component.label,
        value: component.id
      };
    });
  }

  get element() {
    return uiStore.activeElement;
  }

  get baseElement() {
    if (!this.element) return null;
    return configStore.element(this.element.data.type);
  }

  get componentDocumentHref() {
    if (!this.value?.componentId) return false;
    return getDocumentUrlFromId(this.value.componentId);
  }

  get context() {
    return uiStore.inlineEditingContext;
  }

  get isEditDisabled() {
    if (this.loading && !this.selectedComponent) return true;
    return !this.componentDocumentHref;
  }

  get isInlineEditing() {
    return (
      this.context?.blockId === this.value?.componentId &&
      this.context?.elementId === uiStore.activeElementId
    );
  }
  get canEditInline() {
    // A block id can be set, but the block might be deleted.
    if (!this.selectedComponent) return false;

    // Disable inline editing for nested blocks. Instead, redirect to another page.
    if (
      this.context?.blockId &&
      this.context?.blockId !== this.value?.componentId
    ) {
      return false;
    }

    return !!this.baseElement?.nestingRule.inlineEditable;
  }

  get componentControls(): Control[] {
    const block = configStore.globalBlocks.find(
      b => b.id === this.value?.componentId
    );
    if (!block || !block.tree) return [];
    return makeControlsFromComponent(block.tree);
  }

  makeTargets(componentId: number) {
    const block = configStore.globalBlocks.find(b => b.id === componentId);
    if (!block || !block.tree) return [];
    return makeTargetsFromComponent(block.tree);
  }

  edit(string: unknown) {
    const componentId = Number(!string ? "" : string);
    const targets = this.makeTargets(componentId);

    this.$emit("input", {
      ...this.value,
      componentId,
      targets
    });
  }

  showPropertyKeys = false;
  togglePropertyKeys() {
    this.showPropertyKeys = !this.showPropertyKeys;
  }

  toggleInlineEdit(event: MouseEvent) {
    if (this.isInlineEditing) {
      event.preventDefault();
      stopInlineEditing();
    } else if (this.canEditInline) {
      if (event.ctrlKey || event.metaKey) return;
      event.preventDefault();

      if (this.value?.componentId) {
        startInlineEditing({
          elementId: uiStore.activeElementId!,
          blockId: this.value?.componentId
        });
      }
    }

    // If the block is not inline editable, you are redirected to edit the block on a separate page.
  }

  onVisibilityChange() {
    if (document.hidden) return;
    this.loadBlocks();
  }

  mounted() {
    this.loading = true;
    this.loadBlocks();

    document.addEventListener(
      "visibilitychange",
      this.onVisibilityChange,
      false
    );
  }

  beforeDestroy() {
    document.removeEventListener(
      "visibilitychange",
      this.onVisibilityChange,
      false
    );
  }
}
